import React, { useState, useEffect, useRef } from 'react';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import styled from 'styled-components';
import { ArrowLeft, Lock, X, Plus, Upload, Trash2 } from 'lucide-react';
import { resizeImageToBlob } from '../utils/imageResize';
import LocationPicker from './LocationPicker';

const Page = styled.div`
  min-height: 100%;
  background: ${({ theme }) => theme.colors.bg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: -36px -44px;

  @media (max-width: 768px) {
    margin: -24px -16px;
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 14px 40px;
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 160ms, color 160ms, border-color 160ms;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const HeaderTitle = styled.h1`
  flex: 1;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const SaveBtn = styled.button`
  padding: 10px 26px;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  border-radius: 8px;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 160ms;

  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; }
`;

const Body = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 40px 80px;

  @media (max-width: 640px) {
    padding: 28px 16px 60px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Input = styled.input`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const TextArea = styled.textarea`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  min-height: 180px;
  resize: vertical;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.65;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const SelectInput = styled.select`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  cursor: pointer;
  transition: border-color 160ms;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  option {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SlugReadonly = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.9rem;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 6px 0;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

/* Technologies tag input */
const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  min-height: 48px;
  align-items: flex-start;
  align-content: flex-start;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 999px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent};
`;

const TagRemove = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.accent};
  opacity: 0.6;
  display: flex;
  align-items: center;
  line-height: 1;
  transition: opacity 160ms;

  &:hover { opacity: 1; }
`;

const TagInputRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const TagInput = styled(Input)`
  flex: 1;
`;

const AddTagBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  background: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 8px;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 160ms;

  &:hover { opacity: 0.9; }
`;

/* Image section */
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;

const ImageThumb = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveThumbBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 160ms;

  &:hover { background: rgba(239, 68, 68, 0.9); }
`;

const UploadZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 20px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.muted};
  transition: border-color 160ms, color 160ms;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  input { display: none; }
  p { margin: 0; font-size: 0.85rem; text-align: center; }
`;

const StatusMsg = styled.p`
  font-size: 0.85rem;
  color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.text.muted)};
  margin: 0;
`;


const ProjectEditPage = ({ project, onClose }) => {
  const isEditing = Boolean(project);

  const [form, setForm] = useState({
    title: '',
    projectId: '',
    type: 'mobile',
    tagline: '',
    description: '',
    technologies: [],
    iosLink: '',
    androidLink: '',
    webLink: '',
    images: [],
    coverImage: '',
    city: '',
    country: '',
    lat: '',
    lng: '',
  });

  const [techInput, setTechInput] = useState('');
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const techInputRef = useRef(null);

  useEffect(() => {
    if (project) {
      setForm({
        title: project.title || '',
        projectId: project.id || '',
        type: project.type || 'mobile',
        tagline: project.tagline || '',
        description: project.description || '',
        technologies: project.technologies || [],
        iosLink: project.iosLink || '',
        androidLink: project.androidLink || '',
        webLink: project.webLink || '',
        images: project.images || [],
        coverImage: project.coverImage || '',
        city: project.city || '',
        country: project.country || '',
        lat: project.coordinates?.lat != null ? String(project.coordinates.lat) : '',
        lng: project.coordinates?.lng != null ? String(project.coordinates.lng) : '',
      });
    }
  }, [project]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const addTech = () => {
    const val = techInput.trim();
    if (!val || form.technologies.includes(val)) { setTechInput(''); return; }
    set('technologies', [...form.technologies, val]);
    setTechInput('');
    techInputRef.current?.focus();
  };

  const removeTech = (tech) => set('technologies', form.technologies.filter(t => t !== tech));

  const handleTechKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addTech(); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setNewImageFiles(prev => [...prev, ...files]);
  };

  const removeExistingImage = (index) => {
    set('images', form.images.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setCoverFile(file);
  };

  const removeCoverFile = () => setCoverFile(null);

  const removeExistingCover = () => set('coverImage', '');

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Upload timed out after ${ms / 1000}s — check your network or Firebase Storage CORS config`)), ms)
      ),
    ]);

  const uploadImages = async (projectId) => {
    const fullUrls = [];
    const thumbUrls = [];
    const webpMeta = { contentType: 'image/webp' };

    for (const file of newImageFiles) {
      const ts = Date.now();
      const baseName = file.name.replace(/\.[^.]+$/, '');

      const [fullBlob, thumbBlob] = await Promise.all([
        resizeImageToBlob(file, 1200, 0.85),
        resizeImageToBlob(file, 300, 0.80),
      ]);

      const fullRef  = ref(storage, `projects/${projectId}/${ts}_${baseName}.webp`);
      const thumbRef = ref(storage, `thumbnails/${projectId}/${ts}_${baseName}.webp`);

      await Promise.all([
        withTimeout(uploadBytes(fullRef, fullBlob, webpMeta), 30000),
        withTimeout(uploadBytes(thumbRef, thumbBlob, webpMeta), 30000),
      ]);

      const [fullUrl, thumbUrl] = await Promise.all([
        withTimeout(getDownloadURL(fullRef), 15000),
        withTimeout(getDownloadURL(thumbRef), 15000),
      ]);

      fullUrls.push(fullUrl);
      thumbUrls.push(thumbUrl);
    }

    return { fullUrls, thumbUrls };
  };

  const uploadCover = async (projectId) => {
    if (!coverFile) return null;
    const ts = Date.now();
    const baseName = coverFile.name.replace(/\.[^.]+$/, '');
    const blob = await resizeImageToBlob(coverFile, 960, 0.85);
    const coverRef = ref(storage, `covers/${projectId}/${ts}_${baseName}.webp`);
    await withTimeout(uploadBytes(coverRef, blob, { contentType: 'image/webp' }), 30000);
    return withTimeout(getDownloadURL(coverRef), 15000);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!isEditing && !form.projectId.trim()) { setError('Project ID is required.'); return; }
    if (!isEditing && !/^[a-z0-9-]+$/.test(form.projectId)) {
      setError('Project ID must be lowercase letters, numbers, and hyphens only.');
      return;
    }
    if (!form.description.trim()) { setError('Description is required.'); return; }

    setError('');
    setSaving(true);

    try {
      const docId = isEditing ? project.id : form.projectId;
      const { fullUrls, thumbUrls } = await uploadImages(docId);
      const allImages     = [...form.images, ...fullUrls];
      const allThumbnails = [...(form.thumbnails || []), ...thumbUrls];

      const coverUrl = await uploadCover(docId);
      const finalCoverImage = coverUrl || form.coverImage || '';

      const latNum = form.lat === '' ? null : Number(form.lat);
      const lngNum = form.lng === '' ? null : Number(form.lng);
      const hasCoords =
        latNum != null && lngNum != null && !Number.isNaN(latNum) && !Number.isNaN(lngNum);

      const data = {
        title: form.title.trim(),
        type: form.type,
        tagline: form.tagline.trim(),
        description: form.description.trim(),
        technologies: form.technologies,
        iosLink: form.iosLink.trim(),
        androidLink: form.androidLink.trim(),
        webLink: form.webLink.trim(),
        images: allImages,
        thumbnails: allThumbnails,
        coverImage: finalCoverImage,
        city: form.city.trim(),
        country: form.country.trim(),
        coordinates: hasCoords ? { lat: latNum, lng: lngNum } : null,
        updatedAt: new Date().toISOString(),
      };

      if (isEditing) {
        await updateDoc(doc(db, 'projects', project.id), data);
      } else {
        await setDoc(doc(db, 'projects', docId), { ...data, createdAt: new Date().toISOString() });
      }

      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <StickyHeader>
        <BackBtn onClick={onClose} aria-label="Back to dashboard">
          <ArrowLeft size={18} />
        </BackBtn>
        <HeaderTitle>{isEditing ? `Editing: ${project.title}` : 'New Project'}</HeaderTitle>
        <SaveBtn onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </SaveBtn>
      </StickyHeader>

      <Body>
        <Section>

          {/* Title */}
          <Field>
            <Label>Project Title *</Label>
            <Input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g., AITOPIA"
            />
          </Field>

          {/* Project ID */}
          <Field>
            <Label>Project ID / Slug *</Label>
            {isEditing ? (
              <SlugReadonly>
                <Lock size={14} />
                {form.projectId}
              </SlugReadonly>
            ) : (
              <Input
                value={form.projectId}
                onChange={e => set('projectId', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., aitopia"
              />
            )}
          </Field>

          {/* Type */}
          <Field>
            <Label>Type *</Label>
            <SelectInput value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="mobile">Mobile App</option>
              <option value="web">Web Application</option>
            </SelectInput>
          </Field>

          <Divider />

          {/* Tagline */}
          <Field>
            <Label>Tagline</Label>
            <Input
              value={form.tagline}
              onChange={e => set('tagline', e.target.value)}
              placeholder="One-line summary shown under the title"
            />
          </Field>

          {/* Description */}
          <Field>
            <Label>Description *</Label>
            <TextArea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Describe the project in detail…"
            />
          </Field>

          <Divider />

          {/* Technologies */}
          <Field>
            <Label>Technologies</Label>
            {form.technologies.length > 0 && (
              <TagsWrap>
                {form.technologies.map(tech => (
                  <Tag key={tech}>
                    {tech}
                    <TagRemove type="button" onClick={() => removeTech(tech)}>
                      <X size={11} />
                    </TagRemove>
                  </Tag>
                ))}
              </TagsWrap>
            )}
            <TagInputRow>
              <TagInput
                ref={techInputRef}
                value={techInput}
                onChange={e => setTechInput(e.target.value)}
                onKeyDown={handleTechKeyDown}
                placeholder="React Native, Swift, Firebase…"
              />
              <AddTagBtn type="button" onClick={addTech}>
                <Plus size={14} />
                Add
              </AddTagBtn>
            </TagInputRow>
          </Field>

          <Divider />

          {/* Links */}
          <Field>
            <Label>Store &amp; Web Links</Label>
            <TwoCol>
              <Field>
                <Label>iOS / App Store</Label>
                <Input
                  type="url"
                  value={form.iosLink}
                  onChange={e => set('iosLink', e.target.value)}
                  placeholder="https://apps.apple.com/…"
                />
              </Field>
              <Field>
                <Label>Android / Google Play</Label>
                <Input
                  type="url"
                  value={form.androidLink}
                  onChange={e => set('androidLink', e.target.value)}
                  placeholder="https://play.google.com/…"
                />
              </Field>
            </TwoCol>
            <Field>
              <Label>Website</Label>
              <Input
                type="url"
                value={form.webLink}
                onChange={e => set('webLink', e.target.value)}
                placeholder="https://…"
              />
            </Field>
          </Field>

          <Divider />

          {/* Location (for globe pin) */}
          <Field>
            <Label>Location (for the About-section globe pin — optional)</Label>
            <LocationPicker
              value={{ city: form.city, country: form.country, lat: form.lat, lng: form.lng }}
              onChange={(loc) => setForm(prev => ({
                ...prev,
                city: loc.city,
                country: loc.country,
                lat: loc.lat === '' ? '' : String(loc.lat),
                lng: loc.lng === '' ? '' : String(loc.lng),
              }))}
            />
          </Field>

          <Divider />

          {/* Project Detail Image (960×720) */}
          <Field>
            <Label>Project Detail Image (960×720)</Label>

            {(form.coverImage || coverFile) && (
              <ImageGrid>
                {form.coverImage && !coverFile && (
                  <ImageThumb>
                    <img src={form.coverImage} alt="Project detail" />
                    <RemoveThumbBtn type="button" onClick={removeExistingCover}>
                      <Trash2 size={12} />
                    </RemoveThumbBtn>
                  </ImageThumb>
                )}
                {coverFile && (
                  <ImageThumb>
                    <img src={URL.createObjectURL(coverFile)} alt="New project detail" />
                    <RemoveThumbBtn type="button" onClick={removeCoverFile}>
                      <Trash2 size={12} />
                    </RemoveThumbBtn>
                  </ImageThumb>
                )}
              </ImageGrid>
            )}

            {!form.coverImage && !coverFile && (
              <UploadZone>
                <Upload size={28} />
                <p>Click to upload project detail image</p>
                <p style={{ opacity: 0.55, fontSize: '0.78rem' }}>960×720 recommended · PNG, JPG, WebP</p>
                <input type="file" accept="image/*" onChange={handleCoverSelect} />
              </UploadZone>
            )}
          </Field>

          <Divider />

          {/* Images */}
          <Field>
            <Label>App Icon Images</Label>

            {(form.images.length > 0 || newImageFiles.length > 0) && (
              <ImageGrid>
                {form.images.map((url, i) => (
                  <ImageThumb key={`existing-${i}`}>
                    <img src={url} alt={`Existing ${i + 1}`} />
                    <RemoveThumbBtn type="button" onClick={() => removeExistingImage(i)}>
                      <Trash2 size={12} />
                    </RemoveThumbBtn>
                  </ImageThumb>
                ))}
                {newImageFiles.map((file, i) => (
                  <ImageThumb key={`new-${i}`}>
                    <img src={URL.createObjectURL(file)} alt={`New ${i + 1}`} />
                    <RemoveThumbBtn type="button" onClick={() => removeNewImage(i)}>
                      <Trash2 size={12} />
                    </RemoveThumbBtn>
                  </ImageThumb>
                ))}
              </ImageGrid>
            )}

            <UploadZone>
              <Upload size={28} />
              <p>Click to upload images</p>
              <p style={{ opacity: 0.55, fontSize: '0.78rem' }}>PNG, JPG, WebP — multiple allowed</p>
              <input type="file" accept="image/*" multiple onChange={handleFileSelect} />
            </UploadZone>
          </Field>

          {error && <StatusMsg $error>{error}</StatusMsg>}

        </Section>
      </Body>
    </Page>
  );
};

export default ProjectEditPage;
